import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Search, Ban, Undo2 } from "lucide-react";
import { RetireDialog } from "@/components/RecordEditor";
import { ModuleNav } from "./ModuleNav";

const EMPTY = {
  title: "", department: "Histology", category: "Other", discovered_on: "", description: "",
  root_cause: "", corrective_action: "", preventive_action: "", responsible_id: "",
  status: "Open", target_date: "", resolution_date: "", cap_item_id: "",
  reference_type: "", reference_label: "",
};

const STATUS_STYLE = {
  Open: "bg-red-800 text-white",
  "In progress": "bg-amber-100 text-amber-900",
  Resolved: "bg-[#134e4a] text-white",
  Verified: "bg-[#134e4a] text-white",
};

export default function Nonconformance() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ q: "", department: "", status: "", category: "", flag: "", voided: "" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });
  const [detail, setDetail] = useState(null);
  const [retireNc, setRetireNc] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const p = { ...filters };
      Object.keys(p).forEach((k) => !p[k] && delete p[k]);
      const { data } = await api.get("/nonconformances", { params: p });
      setRows(data);
    } catch (e) { toast.error(apiError(e)); }
  }, [filters]);

  useEffect(() => {
    api.get("/accreditation/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/cap-items").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const canEdit = user?.role !== "viewer";

  const clean = (body) => {
    const out = { ...body };
    ["discovered_on", "target_date", "resolution_date"].forEach((k) => { if (!out[k]) out[k] = null; });
    ["responsible_id", "cap_item_id"].forEach((k) => { if (!out[k]) out[k] = null; });
    return out;
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy("create");
    try {
      const { data } = await api.post("/nonconformances", clean(form));
      toast.success(`${data.nc_number} logged`);
      setOpen(false);
      setForm({ ...EMPTY });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy("save");
    try {
      await api.put(`/nonconformances/${detail.id}`, clean({
        title: detail.title, department: detail.department, category: detail.category,
        description: detail.description, root_cause: detail.root_cause || "",
        corrective_action: detail.corrective_action || "", preventive_action: detail.preventive_action || "",
        responsible_id: detail.responsible_id || "", status: detail.status,
        target_date: (detail.target_date || "").slice(0, 10),
        resolution_date: (detail.resolution_date || "").slice(0, 10),
        cap_item_id: detail.cap_item_id || "",
      }));
      setDetail(null);
      toast.success("Nonconformance updated");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  return (
    <div className="space-y-6" data-testid="nc-page">
      <ModuleNav
        title="Nonconformance & Corrective Action"
        blurb="Every event that did not meet requirement — what happened, why, what was done about it, who owns it and when it closed."
        actions={canEdit && (
          <Button onClick={() => setOpen(true)} data-testid="new-nc-button"
                  className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
            <Plus size={16} className="mr-2" /> Log nonconformance
          </Button>
        )}
      />

      <div className="flex flex-wrap items-end gap-3 border border-slate-200 bg-slate-50 p-3">
        <div className="min-w-[200px] flex-1">
          <Label className="text-xs uppercase tracking-wide text-slate-600">Search</Label>
          <div className="relative mt-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                   data-testid="nc-search-input" placeholder="NC number, title or description"
                   className="h-11 border-slate-300 pl-9" />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">Department</Label>
          <select value={filters.department} onChange={(e) => setFilters((f) => ({ ...f, department: e.target.value }))}
                  data-testid="nc-department-filter" className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">All departments</option>
            {(meta?.departments || []).map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">Status</Label>
          <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                  data-testid="nc-status-filter" className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">Any status</option>
            {(meta?.nc_statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">Category</Label>
          <select value={filters.category} onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}
                  data-testid="nc-category-filter" className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">All categories</option>
            {(meta?.nc_categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">Flag</Label>
          <select value={filters.flag} onChange={(e) => setFilters((f) => ({ ...f, flag: e.target.value }))}
                  data-testid="nc-flag-filter" className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">No flag</option>
            <option value="open">Still open</option>
            <option value="overdue">Past target date</option>
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">View</Label>
          <select value={filters.voided} onChange={(e) => setFilters((f) => ({ ...f, voided: e.target.value }))}
                  data-testid="nc-voided-filter" className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">Active records</option>
            <option value="true">Retired records</option>
          </select>
        </div>
        <Button variant="outline" data-testid="nc-clear-filters" className="h-11 border-slate-300"
                onClick={() => setFilters({ q: "", department: "", status: "", category: "", flag: "", voided: "" })}>Clear</Button>
      </div>

      <div className="border border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px] text-sm" data-testid="nc-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr className="border-b border-slate-200">
                <th className="px-3 py-2">NC number</th><th className="px-3 py-2">Event</th>
                <th className="px-3 py-2">Department</th><th className="px-3 py-2">Category</th>
                <th className="px-3 py-2">Discovered</th><th className="px-3 py-2">Responsible</th>
                <th className="px-3 py-2">Status</th><th className="px-3 py-2">Resolved</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 row-hover hover:bg-slate-50" data-testid={`nc-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <button onClick={() => setDetail({ ...r })} data-testid={`nc-open-${r.id}`}
                            className="font-mono text-xs font-bold text-[#134e4a] hover:underline">{r.nc_number}</button>
                    {r.cap_reference && (
                      <div className="text-[11px] text-slate-500">CAP {r.cap_reference}</div>
                    )}
                  </td>
                  <td className="max-w-[320px] px-3 py-2 text-slate-700">{r.title}</td>
                  <td className="px-3 py-2 text-slate-600">{r.department}</td>
                  <td className="px-3 py-2 text-slate-600">{r.category}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{fmtDate(r.discovered_on)}</td>
                  <td className="px-3 py-2 text-slate-600">{r.responsible || <span className="text-slate-400">unassigned</span>}</td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[r.status]}`}>{r.status}</span>
                    {r.overdue && <span className="ml-1 bg-red-800 px-1.5 py-0.5 text-[11px] font-semibold text-white"
                                        data-testid={`nc-overdue-${r.id}`}>overdue</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">{r.resolution_date ? fmtDate(r.resolution_date) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && (
          <div className="px-3 py-10 text-center" data-testid="nc-empty">
            <p className="text-sm text-slate-500">
              {Object.values(filters).some(Boolean) ? "No records match these filters." : "No nonconformances logged yet."}
            </p>
            {canEdit && (
              <Button onClick={() => setOpen(true)} data-testid="empty-add-nc"
                      className="mt-4 h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
                <Plus size={16} className="mr-2" /> Log the first one
              </Button>
            )}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="new-nc-dialog">
          <DialogHeader>
            <DialogTitle>Log nonconformance</DialogTitle>
            <DialogDescription>The NC number is assigned automatically and never reused.</DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">What happened</Label>
              <Input value={form.title} required minLength={3} placeholder="Short title, e.g. Unlabeled cassette found at the microtome"
                     onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                     data-testid="nc-title-input" className="mt-1 h-11 border-slate-300" />
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Department</Label>
                <select value={form.department} onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))}
                        data-testid="nc-department-input" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                  {(meta?.departments || []).map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Category</Label>
                <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                        data-testid="nc-category-input" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                  {(meta?.nc_categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Discovered on</Label>
                <Input type="date" value={form.discovered_on}
                       onChange={(e) => setForm((f) => ({ ...f, discovered_on: e.target.value }))}
                       data-testid="nc-discovered-input" className="mt-1 h-11 border-slate-300" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Description</Label>
              <Textarea value={form.description} required minLength={5}
                        onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                        data-testid="nc-description-input" className="mt-1 min-h-[80px] border-slate-300" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Root cause</Label>
              <Textarea value={form.root_cause} onChange={(e) => setForm((f) => ({ ...f, root_cause: e.target.value }))}
                        data-testid="nc-root-cause-input" className="mt-1 min-h-[60px] border-slate-300" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Corrective action</Label>
                <Textarea value={form.corrective_action} onChange={(e) => setForm((f) => ({ ...f, corrective_action: e.target.value }))}
                          data-testid="nc-corrective-input" className="mt-1 min-h-[60px] border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Preventive action</Label>
                <Textarea value={form.preventive_action} onChange={(e) => setForm((f) => ({ ...f, preventive_action: e.target.value }))}
                          data-testid="nc-preventive-input" className="mt-1 min-h-[60px] border-slate-300" />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Responsible staff</Label>
                <select value={form.responsible_id} onChange={(e) => setForm((f) => ({ ...f, responsible_id: e.target.value }))}
                        data-testid="nc-responsible-input" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                  <option value="">Unassigned</option>
                  {(meta?.staff || []).map((s) => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Status</Label>
                <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                        data-testid="nc-status-input" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                  {(meta?.nc_statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Target close date</Label>
                <Input type="date" value={form.target_date}
                       onChange={(e) => setForm((f) => ({ ...f, target_date: e.target.value }))}
                       data-testid="nc-target-input" className="mt-1 h-11 border-slate-300" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Link to a CAP checklist item (optional)</Label>
              <select value={form.cap_item_id} onChange={(e) => setForm((f) => ({ ...f, cap_item_id: e.target.value }))}
                      data-testid="nc-cap-item-input" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                <option value="">Not linked</option>
                {items.map((i) => <option key={i.id} value={i.id}>{i.reference} — {i.requirement.slice(0, 60)}</option>)}
              </select>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Related record type</Label>
                <Input value={form.reference_type} placeholder="Case / Reagent / Instrument"
                       onChange={(e) => setForm((f) => ({ ...f, reference_type: e.target.value }))}
                       data-testid="nc-ref-type-input" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Related record</Label>
                <Input value={form.reference_label} placeholder="e.g. S26-00042 or Processor 1"
                       onChange={(e) => setForm((f) => ({ ...f, reference_label: e.target.value }))}
                       data-testid="nc-ref-label-input" className="mt-1 h-11 border-slate-300" />
              </div>
            </div>
            <Button type="submit" disabled={busy === "create"} data-testid="create-nc-submit"
                    className="h-11 w-full bg-[#134e4a] hover:bg-[#0f3f3c]">
              {busy === "create" ? "Saving…" : "Log nonconformance"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="nc-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-mono">{detail?.nc_number}</DialogTitle>
            <DialogDescription>
              Logged by {detail?.logged_by || "—"} · discovered {detail ? fmtDate(detail.discovered_on) : ""}
              {detail?.reference_label ? ` · ${detail.reference_type || "Related"}: ${detail.reference_label}` : ""}
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <form onSubmit={save} className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">What happened</Label>
                <Input value={detail.title} disabled={!canEdit}
                       onChange={(e) => setDetail((d) => ({ ...d, title: e.target.value }))}
                       data-testid="nc-detail-title" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Description</Label>
                <Textarea value={detail.description} disabled={!canEdit}
                          onChange={(e) => setDetail((d) => ({ ...d, description: e.target.value }))}
                          data-testid="nc-detail-description" className="mt-1 min-h-[70px] border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Root cause</Label>
                <Textarea value={detail.root_cause || ""} disabled={!canEdit}
                          onChange={(e) => setDetail((d) => ({ ...d, root_cause: e.target.value }))}
                          data-testid="nc-detail-root-cause" className="mt-1 min-h-[60px] border-slate-300" />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Corrective action</Label>
                  <Textarea value={detail.corrective_action || ""} disabled={!canEdit}
                            onChange={(e) => setDetail((d) => ({ ...d, corrective_action: e.target.value }))}
                            data-testid="nc-detail-corrective" className="mt-1 min-h-[60px] border-slate-300" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Preventive action</Label>
                  <Textarea value={detail.preventive_action || ""} disabled={!canEdit}
                            onChange={(e) => setDetail((d) => ({ ...d, preventive_action: e.target.value }))}
                            data-testid="nc-detail-preventive" className="mt-1 min-h-[60px] border-slate-300" />
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-4">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Responsible</Label>
                  <select value={detail.responsible_id || ""} disabled={!canEdit}
                          onChange={(e) => setDetail((d) => ({ ...d, responsible_id: e.target.value }))}
                          data-testid="nc-detail-responsible" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                    <option value="">Unassigned</option>
                    {(meta?.staff || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Status</Label>
                  <select value={detail.status} disabled={!canEdit}
                          onChange={(e) => setDetail((d) => ({ ...d, status: e.target.value }))}
                          data-testid="nc-detail-status" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                    {(meta?.nc_statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Target date</Label>
                  <Input type="date" value={(detail.target_date || "").slice(0, 10)} disabled={!canEdit}
                         onChange={(e) => setDetail((d) => ({ ...d, target_date: e.target.value }))}
                         data-testid="nc-detail-target" className="mt-1 h-11 border-slate-300" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Resolution date</Label>
                  <Input type="date" value={(detail.resolution_date || "").slice(0, 10)} disabled={!canEdit}
                         onChange={(e) => setDetail((d) => ({ ...d, resolution_date: e.target.value }))}
                         data-testid="nc-detail-resolution" className="mt-1 h-11 border-slate-300" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Linked CAP checklist item</Label>
                <select value={detail.cap_item_id || ""} disabled={!canEdit}
                        onChange={(e) => setDetail((d) => ({ ...d, cap_item_id: e.target.value }))}
                        data-testid="nc-detail-cap-item" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                  <option value="">Not linked</option>
                  {items.map((i) => <option key={i.id} value={i.id}>{i.reference} — {i.requirement.slice(0, 60)}</option>)}
                </select>
              </div>
              {canEdit && (
                <div className="flex flex-wrap gap-2">
                  <Button type="submit" disabled={busy === "save"} data-testid="nc-detail-save"
                          className="h-11 flex-1 bg-[#134e4a] hover:bg-[#0f3f3c]">
                    {busy === "save" ? "Saving…" : "Save record"}
                  </Button>
                  {detail.voided ? (
                    <Button type="button" variant="outline" data-testid="nc-restore-button" className="h-11 border-slate-300"
                            onClick={async () => {
                              try {
                                await api.post(`/nonconformances/${detail.id}/restore`);
                                toast.success("Record reinstated");
                                setDetail(null);
                                load();
                              } catch (e) { toast.error(apiError(e)); }
                            }}>
                      <Undo2 size={15} className="mr-2" /> Reinstate
                    </Button>
                  ) : (
                    <Button type="button" variant="outline" onClick={() => setRetireNc(detail)}
                            data-testid="nc-retire-button" className="h-11 border-red-300 text-red-800 hover:bg-red-50">
                      <Ban size={15} className="mr-2" /> Retire
                    </Button>
                  )}
                </div>
              )}
            </form>
          )}
        </DialogContent>
      </Dialog>
      <RetireDialog
        open={!!retireNc} onOpenChange={(v) => !v && setRetireNc(null)} testid="retire-nc-dialog"
        title={`Retire ${retireNc?.nc_number || ""}`} confirmLabel="Retire record"
        description="Use this when a nonconformance was logged in error or duplicated. It stays on file under the Retired view."
        onConfirm={async (reason) => {
          await api.post(`/nonconformances/${retireNc.id}/void`, { reason });
          toast.success("Record retired");
          setDetail(null);
          load();
        }}
      />
    </div>
  );
}
